// src/services/svg/initialization.ts

import { ref, listAll } from "firebase/storage";
import { storage, db } from "../../lib/firebase";
import { useDebugStore } from "../../store/debugStore";
import { useUserStore } from "../../store/userStore";
import { UniversalDirectoryManager } from "../storage/universalDirectoryManager";
import { doc, getDoc } from "firebase/firestore";
import { QRCodeInitialization } from "../qrcode/initialization";
import { QRCodeService } from "../qrCodeService";

export class SVGInitializationService {
  /**
   * Validates if all required SVG directories exist with valid QR code SVGs
   */
  static async validateInitialization(userId: string): Promise<{
    isInitialized: boolean;
    missingDirectories: string[];
  }> {
    const missingDirectories: string[] = [];
    const categories = UniversalDirectoryManager.STRUCTURE.categoryFolders.svg;

    console.log(`Validating SVG directory initialization for user: ${userId}`);

    try {
      // Check each category directory for SVG files
      for (const category of categories) {
        const svgPath = UniversalDirectoryManager.getPath({
          userId,
          contentType: "svg",
          category,
        });

        const categoryRef = ref(storage, svgPath);

        try {
          // List files in directory
          const files = await listAll(categoryRef);

          // Check if directory has a valid QR code SVG for this category
          const hasValidQRCode = files.items.some((item) => {
            return this.isValidSVGFilename(item.name, userId, category);
          });

          if (!hasValidQRCode) {
            console.warn(`No valid QR code SVG in directory: ${category}`);
            missingDirectories.push(category);
          }
        } catch (error) {
          console.warn(`Missing or empty SVG directory: ${category}`);
          missingDirectories.push(category);
        }
      }

      const isInitialized = missingDirectories.length === 0;
      return { isInitialized, missingDirectories };
    } catch (error) {
      console.error("Error validating SVG directories:", error);
      return { isInitialized: false, missingDirectories: categories };
    }
  }

  /**
   * Initializes the SVG directory structure for a new user
   */
  static async initializeUserSVGDirectories(userId: string): Promise<boolean> {
    const addDebugMessage = useDebugStore.getState().addMessage;
    const categories = UniversalDirectoryManager.STRUCTURE.categoryFolders.svg;

    try {
      addDebugMessage("info", "Initializing SVG directories");

      // Create each category directory with an example SVG
      const failedCategories: string[] = [];
      for (const category of categories) {
        // Create example SVG instead of placeholder
        const created = await this.createExampleSVG(userId, category);
        if (!created) {
          failedCategories.push(category);
        }
      }

      if (failedCategories.length > 0) {
        throw new Error(
          `Failed to initialize SVG categories: ${failedCategories.join(", ")}`,
        );
      }

      addDebugMessage("success", "SVG directories initialized successfully");
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      addDebugMessage(
        "error",
        `Failed to initialize SVG directories: ${errorMessage}`,
      );
      return false;
    }
  }

  /**
   * Verifies and initializes SVG directories if needed
   */
  static async verifyAndInitializeSVGDirectories(
    userId: string,
  ): Promise<boolean> {
    try {
      const { isInitialized, missingDirectories } =
        await this.validateInitialization(userId);

      if (!isInitialized) {
        console.log("SVG directories need initialization:", missingDirectories);
        return await this.initializeUserSVGDirectories(userId);
      }

      return true;
    } catch (error) {
      console.error("Error verifying SVG directories:", error);
      return false;
    }
  }

  /**
   * Creates example SVG content for a category if it's empty
   */
  static async createExampleSVG(
    userId: string,
    category: string,
  ): Promise<boolean> {
    const addDebugMessage = useDebugStore.getState().addMessage;
    const userProfile = useUserStore.getState().profile;

    try {
      // First check if we already have a valid QR code SVG
      const svgPath = UniversalDirectoryManager.getPath({
        userId,
        contentType: "svg",
        category,
      });

      const categoryRef = ref(storage, svgPath);
      const files = await listAll(categoryRef);

      // Check for existing valid QR code
      const hasValidQRCode = files.items.some((item) =>
        this.isValidSVGFilename(item.name, userId, category),
      );

      if (hasValidQRCode) {
        addDebugMessage(
          "info",
          `Valid QR code SVG already exists for ${category}`,
        );
        return true;
      }

      // Get vCard path using UniversalDirectoryManager
      const vCardPath = UniversalDirectoryManager.getPath({
        userId,
        contentType: "vcard",
        category,
      });

      // Get vCard data
      const vCardRef = doc(db, vCardPath);
      const vCardDoc = await getDoc(vCardRef);

      if (!vCardDoc.exists()) {
        addDebugMessage("error", `No vCard found for category: ${category}`);
        return false;
      }

      const vCardData = vCardDoc.data() as {
        firstName?: string;
        lastName?: string;
      };
      // Get names from vCard, fallback to profile, then to defaults
      const firstName =
        vCardData.firstName ?? userProfile?.firstName ?? "Unknown";
      const lastName = vCardData.lastName ?? userProfile?.lastName ?? "User";

      // Generate QR code from vCard data
      try {
        // Read an existing token — do NOT create one here to avoid duplicates.
        // Token creation is handled exclusively by initializeDefaultQRCodes.
        const existingUrl = await QRCodeService.getTokenUrl(
          vCardDoc.id,
          userId,
        );
        const qrContent =
          existingUrl ?? `${window.location.origin}/qr/${category}/${userId}`;

        await QRCodeInitialization.createQRCode({
          userId,
          category,
          content: qrContent,
          name: `${firstName}_${lastName}`,
          description: `QR Code for ${category} vCard`,
        });

        addDebugMessage(
          "success",
          `Created QR code SVG for ${category} vCard: ${firstName} ${lastName}`,
        );
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        addDebugMessage("error", `Failed to create QR code: ${errorMessage}`);
        return false;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      addDebugMessage(
        "error",
        `Failed to process vCard for ${category}: ${errorMessage}`,
      );
      return false;
    }
  }

  /**
   * Checks if a filename matches the expected pattern without considering the unique ID
   */
  private static isValidSVGFilename(
    filename: string,
    userId: string,
    category: string,
  ): boolean {
    // Pattern: user_[userId]_[firstName]_[lastName]_[category]_[optionalItem]_[date]_[uniqueId].svg
    const normalizedCategory = category.toLowerCase();
    const pattern = new RegExp(
      `^user_${userId}_.*_.*_${normalizedCategory}(?:_[a-z0-9-]+)?_\\d{8}_[A-Za-z0-9_-]+\\.svg$`,
      "i",
    );
    return pattern.test(filename) && !filename.includes("placeholder");
  }
}
